import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ZoomIn, ZoomOut, Search } from "lucide-react";
import { AREAS, MAP_W, MAP_H, IDF_BBOX, FRESNES } from "../../data/mapPaths";

const NEIGHBOR_DEPTS = {
  "Hauts-de-France": ["02", "59", "60", "62", "80"], Normandie: ["14", "27", "50", "61", "76"],
  "Centre-Val de Loire": ["18", "28", "36", "37", "41", "45"], "Bourgogne-Franche-Comté": ["21", "25", "39", "58", "70", "71", "89", "90"],
  "Grand Est": ["08", "10", "51", "52", "54", "55", "57", "67", "68", "88"],
};
const IDF_CODES = ["75", "77", "78", "91", "92", "93", "94", "95"];
export const slug = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
const keyOf = (a) => (a.kind === "dept" ? a.code : slug(a.name));

const viewFor = (zoom) => {
  if (!zoom) return { s: 1, tx: 0, ty: 0 };
  const [x0, y0, x1, y1] = IDF_BBOX;
  const s = Math.min(MAP_W / (x1 - x0), MAP_H / (y1 - y0)) * 0.86;
  return { s, tx: MAP_W / 2 - ((x0 + x1) / 2) * s, ty: MAP_H / 2 - ((y0 + y1) / 2) * s };
};

export const checkPostal = (cp) => {
  const v = cp.trim();
  if (!/^\d{5}$/.test(v)) return { ok: false, message: "Code postal invalide" };
  const d = v.slice(0, 2);
  if (IDF_CODES.includes(d)) return { ok: true, status: "in", message: "Nous intervenons dans votre secteur", key: d, zoom: true };
  const region = Object.keys(NEIGHBOR_DEPTS).find((r) => NEIGHBOR_DEPTS[r].includes(d));
  return { ok: true, status: "study", message: "Sur étude — contactez-nous", key: region ? slug(region) : null, zoom: false };
};

const Tooltip = ({ area, view }) => {
  const left = ((area.cx * view.s + view.tx) / MAP_W) * 100;
  const top = ((area.cy * view.s + view.ty) / MAP_H) * 100;
  const isDept = area.kind === "dept";
  return (
    <div data-testid="zone-tooltip" role="status" className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-[calc(100%+14px)] glass !bg-white/90 rounded-2xl px-4 py-3 min-w-[180px] transition-[left,top] duration-300"
      style={{ left: `${Math.min(88, Math.max(12, left))}%`, top: `${Math.max(22, top)}%` }}>
      <p className="font-display text-navy text-[15px] leading-tight" data-testid="zone-tooltip-name">{area.name}</p>
      <p className="text-sm text-slate-600" data-testid="zone-tooltip-number">{isDept ? `Département ${area.code}` : `Région ${area.code}`}</p>
      <p className={`mt-1.5 inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${isDept ? "bg-brand-ink text-white" : "bg-brand-100 text-brand-ink"}`} data-testid="zone-tooltip-status">{isDept ? "Intervention" : "Sur demande"}</p>
    </div>
  );
};

export const ZoneMap = ({ compact = false }) => {
  const [zoom, setZoom] = useState(false);
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);
  const [cp, setCp] = useState("");
  const [result, setResult] = useState(null);
  const view = useMemo(() => viewFor(zoom), [zoom]);
  const activeKey = hover || selected;
  const active = AREAS.find((a) => a.kind !== "other" && keyOf(a) === activeKey);

  const submit = (e) => {
    e.preventDefault();
    const r = checkPostal(cp);
    setResult(r);
    if (r.ok) { setSelected(r.key); setZoom(r.zoom); }
  };
  const fill = (a) => {
    const k = keyOf(a);
    if (a.kind === "other") return "#F2F6FB";
    if (k === activeKey) return "#3D6593";
    return a.kind === "dept" ? "#7FA0C6" : "#C9D9EA";
  };
  const labelSize = 13 / view.s;

  return (
    <div data-testid="zone-map" className={`grid gap-6 ${compact ? "lg:grid-cols-12" : "lg:grid-cols-12"}`}>
      <div className="lg:col-span-8 relative rounded-[32px] border border-brand/15 bg-gradient-to-b from-white to-brand-50 overflow-hidden">
        <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} className="w-full h-auto block touch-manipulation" role="group" aria-label="Carte de la zone d'intervention" onMouseLeave={() => setHover(null)}>
          <g style={{ transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.s})`, transformOrigin: "0 0", transition: "transform .9s cubic-bezier(.2,.7,.2,1)" }}>
            {AREAS.map((a) => {
              const k = keyOf(a);
              const interactive = a.kind !== "other";
              return (
                <path key={`${a.kind}-${a.code}`} d={a.d} fill={fill(a)} stroke="#FFFFFF" strokeWidth={a.kind === "dept" ? 1.2 : 1.6} vectorEffect="non-scaling-stroke"
                  strokeLinejoin="round" className={interactive ? "map-area" : ""}
                  {...(interactive ? {
                    "data-testid": `zone-area-${k}`, tabIndex: 0, role: "button",
                    "aria-label": `${a.name} — ${a.kind === "dept" ? `département ${a.code}, intervention` : "sur demande"}`,
                    "aria-pressed": selected === k,
                    onMouseEnter: () => setHover(k), onFocus: () => setHover(k), onBlur: () => setHover(null),
                    onClick: () => setSelected(k), onKeyDown: (e) => e.key === "Enter" && setSelected(k),
                  } : { "aria-hidden": true })} />
              );
            })}
            {zoom && AREAS.filter((a) => a.kind === "dept").map((a) => (
              <text key={`l-${a.code}`} x={a.cx} y={a.cy} textAnchor="middle" dominantBaseline="middle" fontSize={labelSize} fill="#FFFFFF" fontFamily="Lexend" pointerEvents="none">{a.code}</text>
            ))}
            {!zoom && AREAS.filter((a) => a.kind === "region").map((a) => (
              <text key={`r-${a.code}`} x={a.cx} y={a.cy} textAnchor="middle" fontSize={labelSize * 1.05} fill="#10243E" fontFamily="Lexend" opacity="0.7" pointerEvents="none">{a.name}</text>
            ))}
            <g data-testid="zone-fresnes-pin" pointerEvents="none">
              <circle cx={FRESNES[0]} cy={FRESNES[1]} r={6 / view.s} fill="#10243E" className="pin-pulse" />
              <circle cx={FRESNES[0]} cy={FRESNES[1]} r={4.5 / view.s} fill="#10243E" stroke="#fff" strokeWidth={1.5 / view.s} />
              {zoom && <text x={FRESNES[0] + 8 / view.s} y={FRESNES[1] + 4 / view.s} fontSize={labelSize * 0.95} fill="#10243E" fontFamily="Lexend">Siège — Fresnes</text>}
            </g>
          </g>
        </svg>
        {active && <Tooltip area={active} view={view} />}
        <div className="absolute left-4 top-4 flex gap-2">
          <button type="button" onClick={() => setZoom((z) => !z)} className="glass rounded-full px-4 py-2 text-sm font-display text-navy inline-flex items-center gap-2" data-testid="zone-zoom-toggle">
            {zoom ? <><ZoomOut className="h-4 w-4" aria-hidden="true" /> Régions limitrophes</> : <><ZoomIn className="h-4 w-4" aria-hidden="true" /> Zoom Île-de-France</>}
          </button>
        </div>
        <div className="hidden sm:block absolute right-4 bottom-4 glass rounded-2xl px-4 py-3 text-xs text-slate-700 space-y-1.5">
          <p className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-[#7FA0C6]" /> Intervention (Île-de-France)</p>
          <p className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-[#C9D9EA]" /> Sur demande</p>
          <p className="flex items-center gap-2"><MapPin className="h-3 w-3 text-navy" aria-hidden="true" /> Siège — Fresnes (94)</p>
        </div>
      </div>
      <div className="lg:col-span-4 flex flex-col gap-4">
        <div className="sm:hidden flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-700 -mt-2" data-testid="zone-legend-mobile">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[#7FA0C6]" /> Intervention</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[#C9D9EA]" /> Sur demande</span>
          <span className="flex items-center gap-1.5"><MapPin className="h-3 w-3 text-navy" aria-hidden="true" /> Siège — Fresnes</span>
        </div>
        <form onSubmit={submit} noValidate className="rounded-3xl border border-brand/15 bg-white p-6" data-testid="zone-postal-form">
          <label htmlFor="zone-cp" className="font-display text-navy text-lg">Votre code postal</label>
          <p className="text-sm text-slate-600 mt-1">Vérifiez en une seconde si nous intervenons chez vous.</p>
          <div className="mt-4 flex gap-2">
            <input id="zone-cp" inputMode="numeric" maxLength={6} value={cp} onChange={(e) => setCp(e.target.value)} placeholder="94260"
              className="field-input" data-testid="zone-postal-input" aria-describedby="zone-cp-result" />
            <button type="submit" className="btn-accent px-4" data-testid="zone-postal-submit" aria-label="Vérifier"><Search className="h-5 w-5" aria-hidden="true" /></button>
          </div>
          {result && (
            <p id="zone-cp-result" role="status" data-testid="zone-postal-result"
              className={`mt-4 rounded-2xl px-4 py-3 font-display text-[15px] ${!result.ok ? "bg-red-50 text-red-800" : result.status === "in" ? "bg-brand-ink text-white" : "bg-brand-100 text-navy"}`}>
              {result.message}
            </p>
          )}
          {result?.status === "study" && <Link to="/contact" className="mt-3 inline-block link-u text-navy text-sm font-semibold">Nous contacter</Link>}
        </form>
        <div className="rounded-3xl bg-navy text-white p-6">
          <p className="eyebrow !text-brand-200">Couverture</p>
          <p className="mt-2 font-display text-2xl">8 départements</p>
          <p className="text-white/75 text-[15px]">en Île-de-France, et 5 régions limitrophes sur demande.</p>
        </div>
      </div>
    </div>
  );
};
